import React, { useState } from 'react';
import axios from 'axios';
import { X, Send, Loader2 } from 'lucide-react';
import { companyData } from '../data/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PartnerModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const resetAndClose = () => {
    setSubmitted(false);
    setErrorMsg('');
    setFormData({ name: '', email: '', company: '', role: '', message: '' });
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMsg('');

    try {
      await axios.post(`${API}/contact/partner`, formData);
      setSubmitted(true);
      setTimeout(resetAndClose, 4000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorMsg(
        typeof detail === 'string'
          ? detail
          : 'Something went wrong. Please try again or email us directly.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="partner-modal">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={resetAndClose}
        data-testid="partner-modal-backdrop"
      />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-300">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="font-serif text-2xl font-bold text-[#1a365d]">
            Partner With Us
          </h2>
          <button
            onClick={resetAndClose}
            className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
            data-testid="partner-modal-close-btn"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {submitted ? (
            <div className="text-center py-12" data-testid="partner-success-message">
              <div className="w-16 h-16 bg-[#e8f5f3] rounded-full flex items-center justify-center mx-auto mb-4">
                <Send className="w-8 h-8 text-[#2f9e8e]" />
              </div>
              <h3 className="text-xl font-semibold text-[#1a365d] mb-2">Inquiry Received</h3>
              <p className="text-gray-600">
                Thank you. Our team will review your message and reach out shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="partner-form">
              <p className="text-gray-600 text-sm mb-6">
                Fill out the form below and our team will reach out to you at the email provided. Inquiries are sent to <span className="font-medium text-[#2f9e8e]">{companyData.contactEmail}</span>.
              </p>

              {errorMsg && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" data-testid="partner-error-message">
                  {errorMsg}
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e] focus:border-transparent transition-all"
                    placeholder="John Smith"
                    data-testid="partner-input-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e] focus:border-transparent transition-all"
                    placeholder="john@company.com"
                    data-testid="partner-input-email"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Company *</label>
                  <input
                    type="text"
                    name="company"
                    required
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e] focus:border-transparent transition-all"
                    placeholder="Company Name"
                    data-testid="partner-input-company"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Role</label>
                  <input
                    type="text"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e] focus:border-transparent transition-all"
                    placeholder="Managing Partner"
                    data-testid="partner-input-role"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Message *</label>
                <textarea
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e] focus:border-transparent transition-all resize-none"
                  placeholder="Tell us about your platform and what you're looking for in a capital partner..."
                  data-testid="partner-input-message"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#2f9e8e] hover:bg-[#268578] text-white py-3.5 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-70"
                data-testid="partner-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Inquiry
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerModal;
